import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { schoolAPI } from '../services/api';
import PageHeader from './PageHeader';
import DeleteIconButton from './DeleteIconButton';
import SchoolForm from './SchoolForm';
import { hasPermission } from '../utils/permissions';
import './ListWithDetail.css';
import './SchoolList.css';

const PAGE_SIZE = 25;

function SchoolList() {
  const navigate = useNavigate();
  const { id: detailId } = useParams();
  const { tenantPath } = useTenantFromUrl();
  const [schools, setSchools] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchParams] = useSearchParams();
  const [districtSearch, setDistrictSearch] = useState('');
  const [districtNameSearch, setDistrictNameSearch] = useState(searchParams.get('q') || '');
  const sentinelRef = useRef(null);
  const filtersRef = useRef({ districtSearch, districtNameSearch });
  filtersRef.current = { districtSearch, districtNameSearch };
  const page1RequestIdRef = useRef(0);

  const canCreateSchool = hasPermission('schools.create');
  const canDeleteSchool = hasPermission('schools.delete');

  const loadSchools = useCallback(async (pageNum = 1, append = false) => {
    const snap = { ...filtersRef.current };
    let page1Id;
    if (pageNum === 1 && !append) {
      page1Id = ++page1RequestIdRef.current;
    }
    try {
      if (pageNum === 1 && !append) setLoading(true);
      else setLoadingMore(true);
      const params = { page: pageNum, pageSize };
      if (snap.districtSearch.trim()) params.q = snap.districtSearch.trim();
      if (snap.districtNameSearch.trim()) params.district_name_q = snap.districtNameSearch.trim();
      const response = await schoolAPI.getAll(params);
      if (pageNum === 1 && !append) {
        if (page1Id !== page1RequestIdRef.current) return;
      } else if (append) {
        const cur = filtersRef.current;
        if (snap.districtSearch !== cur.districtSearch || snap.districtNameSearch !== cur.districtNameSearch) return;
      }
      const data = response.data.data ?? [];
      const newTotal = response.data.total ?? 0;
      if (append) setSchools((prev) => [...prev, ...data]);
      else setSchools(data);
      setTotal(newTotal);
      setPage(pageNum);
    } catch (error) {
      if (pageNum === 1 && !append && page1Id !== page1RequestIdRef.current) return;
      if (append) {
        const cur = filtersRef.current;
        if (snap.districtSearch !== cur.districtSearch || snap.districtNameSearch !== cur.districtNameSearch) return;
      }
      console.error('Error loading school districts:', error);
      const status = error.response?.status;
      const msg = error.response?.data?.error || error.message || 'Unknown error';
      const details = error.response?.data?.details;
      const detail = status ? ` (${status})` : '';
      const fullMsg = details ? `${msg} ${details}` : msg;
      alert(`Failed to load school districts${detail}. ${fullMsg}`);
    } finally {
      if (pageNum === 1 && !append) {
        if (page1Id === page1RequestIdRef.current) setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, [pageSize]);

  useDebouncedSearch(`${districtSearch}|${districtNameSearch}`, () => loadSchools(1));

  const hasMore = schools.length < total && total > 0;
  const loadMore = () => { if (!loading && !loadingMore && hasMore) loadSchools(page + 1, true); };
  useInfiniteScroll(sentinelRef, loadMore, hasMore, loading || loadingMore);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this school district?')) {
      return;
    }

    try {
      await schoolAPI.delete(id);
      if (String(id) === String(detailId)) navigate(tenantPath('/lists/schools'));
      setSchools((prev) => prev.filter((s) => s.id !== id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (error) {
      console.error('Error deleting school district:', error);
      alert('Failed to delete school district');
    }
  };

  const colCount = 2 + (canDeleteSchool ? 1 : 0);
  const showPanel = detailId != null;

  return (
    <div className={`list-with-detail ${showPanel ? 'list-with-detail--panel-open' : ''}`}>
    <div className="school-list list-with-detail__list">
      <div className="header">
        <PageHeader title="School Districts">
          {canCreateSchool && (
            <button onClick={() => navigate(tenantPath('/lists/schools/new'))} className="btn-new">+ New School District</button>
          )}
        </PageHeader>
      </div>

      <div className="school-list-pagination-info">
        {loading && schools.length === 0 ? 'Loading...' : total === 0 ? 'No school districts found' : total === 1 ? '1 school district found' : `${total} school districts found`}
      </div>

      <div className="school-list-table-wrap list-table-wrap">
        <table className="school-list-table list-table">
          <thead>
            <tr className="list-header-labels">
              <th>District</th>
              <th>District Name</th>
              {canDeleteSchool && <th>Delete</th>}
            </tr>
            <tr className="list-filter-row school-list-filter-row">
              <th>
                <input
                  type="text"
                  className="school-list-filter-input"
                  placeholder="Search district..."
                  value={districtSearch}
                  onChange={(e) => setDistrictSearch(e.target.value)}
                  aria-label="Filter by district"
                />
              </th>
              <th>
                <input
                  type="text"
                  className="school-list-filter-input"
                  placeholder="Search district name..."
                  value={districtNameSearch}
                  onChange={(e) => setDistrictNameSearch(e.target.value)}
                  aria-label="Filter by district name"
                />
              </th>
              {canDeleteSchool && <th />}
            </tr>
          </thead>
          <tbody>
            {loading && schools.length === 0 ? (
              <tr><td colSpan={colCount} className="no-data loading-cell">Loading school districts...</td></tr>
            ) : schools.length === 0 ? (
              <tr><td colSpan={colCount} className="no-data">No school districts found</td></tr>
            ) : (
              schools.map((school) => (
                <tr key={school.id} onClick={() => navigate(tenantPath(`/lists/schools/${school.id}`))} className="clickable-row">
                  <td>{school.district}</td>
                  <td>{school.district_name}</td>
                  {canDeleteSchool && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <DeleteIconButton className="list-btn-delete" onClick={() => handleDelete(school.id)} />
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {hasMore && <div ref={sentinelRef} className="infinite-scroll-sentinel" aria-hidden="true" />}
      {loadingMore && <div className="infinite-scroll-loading">Loading more...</div>}
    </div>

    {showPanel && (
      <div className="list-with-detail__panel">
        <SchoolForm />
      </div>
    )}
    </div>
  );
}

export default SchoolList;
